import { cloneDeep } from '@apollo/client/utilities';
import { CompanyEntity, forCreate, forDelete, forUpdate } from './common/entity';
import { t, translate } from './translation/Translator';
import { TranslationKey } from './translation/TranslationKey';
import { ObsoleteExportConfiguration } from './export/exportConfiguration';

export enum OrderExportField {
  company = 'company',

  orderId = 'orderId',
  orderCreatedAt = 'orderCreatedAt',
  orderCreatedBy = 'orderCreatedBy',
  orderType = 'orderType',

  destinationStockLocationName = 'destinationStockLocationName',
  destinationStockLocationIdentifier = 'destinationStockLocationIdentifier',

  sourceStockLocationName = 'sourceStockLocationName',
  sourceStockLocationIdentifier = 'sourceStockLocationIdentifier',

  contactName = 'contactName',
  contactLocation = 'contactLocation',
  type = 'type',
  status = 'status',
  externalReferenceId = 'externalReferenceId',
  estimatedTimeOfArrival = 'estimatedTimeOfArrival',
  purchaseOrderNumber = 'purchaseOrderNumber',

  fromBinName = 'fromBinName',
  toBinName = 'toBinName',

  productName = 'productName',
  productNumber = 'productNumber',

  serial = 'serial',
  lpn = 'lpn',
  countryOfOrigin = 'countryOfOrigin',

  lotNumber = 'lotNumber',

  processedBy = 'processedBy',
  processedAt = 'processedAt',

  processedQuantity = 'processedQuantity',

  purchasePrice = 'purchasePrice',
  sellingPrice = 'sellingPrice',
}

export class OrderExportConfigurationCustomField {
  index: number;
  customField: string;

  constructor(customField: string, index: number) {
    this.index = index;
    this.customField = customField;
  }
}

export class OrderExportConfigurationMapping extends OrderExportConfigurationCustomField {
  field: OrderExportField;

  constructor(customField: string, index: number, field: OrderExportField) {
    super(customField, index);
    this.field = field;
  }
}

export class OrderExportConfiguration extends CompanyEntity implements ObsoleteExportConfiguration {
  name!: string;
  filename: string = 'ventory_order_${TIMESTAMP}.csv';
  splitByOrder: boolean = false;
  prefix?: string;
  delimiter: string = ',';
  quote?: string;
  fieldMapping: OrderExportConfigurationMapping[] = [...DEFAULT_MAPPING().values()];
  default: boolean = false;

  forUpdate(): UpdateOrderExportConfigurationInput {
    return UpdateOrderExportConfigurationInput.from(this, UpdateOrderExportConfigurationInput);
  }

  forDelete(): DeleteOrderExportConfigurationInput {
    return DeleteOrderExportConfigurationInput.from(this, DeleteOrderExportConfigurationInput);
  }

  validate(fields: any[]) {
    return this.validateEntity(
      Object.getOwnPropertyNames(this) as (keyof OrderExportConfiguration)[],
      (field: keyof OrderExportConfiguration) => {
        return null;
      },
    );
  }

  constructor(obj: any) {
    if (!obj.companyId) return;
    super(obj.companyId);
    this.default = false;
    Object.assign(this, cloneDeep(obj));
    this.default = obj.default || false;
  }

  withName(name: string) {
    this.name = name;
    return cloneDeep(this);
  }

  withFilename(filename: string) {
    this.filename = filename;
    return cloneDeep(this);
  }

  withPrefix(prefix: string) {
    this.prefix = prefix;
    return cloneDeep(this);
  }

  withDelimiter(delimiter: string) {
    this.delimiter = delimiter;
    return cloneDeep(this);
  }

  withSplitByOrder(split: boolean) {
    this.splitByOrder = split;
    return cloneDeep(this);
  }

  withDefault(value: boolean) {
    this.default = value;
    return cloneDeep(this);
  }
}
export class CreateOrderExportConfigurationInput extends forCreate(OrderExportConfiguration) {}

export class UpdateOrderExportConfigurationInput extends forUpdate(OrderExportConfiguration) {}

export class DeleteOrderExportConfigurationInput extends forDelete(OrderExportConfiguration) {}

const DEFAULT_MAPPING = () => {
  const map = new Map<OrderExportField, OrderExportConfigurationMapping>();
  map.set(OrderExportField.orderId, { customField: 'Order', field: OrderExportField.orderId, index: 0 });
  map.set(OrderExportField.productName, { customField: 'Product Name', field: OrderExportField.productName, index: 1 });
  map.set(OrderExportField.productNumber, {
    customField: 'Product Number',
    field: OrderExportField.productNumber,
    index: 2,
  });
  map.set(OrderExportField.sourceStockLocationName, {
    customField: 'Source Stock Location',
    field: OrderExportField.sourceStockLocationName,
    index: 3,
  });
  map.set(OrderExportField.destinationStockLocationName, {
    customField: 'Destination Stock Location',
    field: OrderExportField.destinationStockLocationName,
    index: 4,
  });
  map.set(OrderExportField.fromBinName, { customField: 'From Bin', field: OrderExportField.fromBinName, index: 5 });
  map.set(OrderExportField.toBinName, { customField: 'To Bin', field: OrderExportField.toBinName, index: 6 });
  map.set(OrderExportField.status, { customField: 'Status', field: OrderExportField.status, index: 7 });
  map.set(OrderExportField.processedAt, { customField: 'Processed At', field: OrderExportField.processedAt, index: 8 });
  map.set(OrderExportField.processedBy, { customField: 'Processed By', field: OrderExportField.processedBy, index: 9 });
  map.set(OrderExportField.processedQuantity, {
    customField: 'Processed Quantity',
    field: OrderExportField.processedQuantity,
    index: 10,
  });
  return map;
};

export function orderExportFieldToString(item: OrderExportField): string {
  switch (item) {
    case OrderExportField.company:
      return 'Company';
    case OrderExportField.orderId:
      return 'Order Id';
    case OrderExportField.orderCreatedAt:
      return 'Order Created At';
    case OrderExportField.orderCreatedBy:
      return 'Order Created By';
    case OrderExportField.destinationStockLocationName:
      return translate({ key: TranslationKey.destinationStockLocationName });
    case OrderExportField.destinationStockLocationIdentifier:
      return translate({ key: TranslationKey.destinationStockLocationIdentifier });
    case OrderExportField.sourceStockLocationName:
      return translate({ key: TranslationKey.sourceStockLocationName });
    case OrderExportField.sourceStockLocationIdentifier:
      return translate({ key: TranslationKey.sourceStockLocationIdentifier });
    case OrderExportField.contactName:
      return 'Contact Name';
    case OrderExportField.contactLocation:
      return 'Contact Location';
    case OrderExportField.type:
      return 'Type';
    case OrderExportField.status:
      return 'Status';
    case OrderExportField.externalReferenceId:
      return 'External Reference Id';
    case OrderExportField.estimatedTimeOfArrival:
      return 'Estimated Time Of Arrival';
    case OrderExportField.purchaseOrderNumber:
      return 'Purchase Order Number';
    case OrderExportField.fromBinName:
      return 'From Bin';
    case OrderExportField.toBinName:
      return 'To Bin';
    case OrderExportField.productName:
      return 'Product Name';
    case OrderExportField.productNumber:
      return 'Product Number';
    case OrderExportField.serial:
      return 'Serial Number';
    case OrderExportField.lpn:
      return 'LPN';
    case OrderExportField.countryOfOrigin:
      return 'Country Of Origin';
    case OrderExportField.lotNumber:
      return 'Lot Number';
    case OrderExportField.processedBy:
      return 'Processed By';
    case OrderExportField.processedAt:
      return 'Processed At';
    case OrderExportField.processedQuantity:
      return 'Processed Quantity';
    case OrderExportField.orderType:
      return 'Order Type';
    case OrderExportField.purchasePrice:
      return t(TranslationKey.purchasePrice).singular.label;
    case OrderExportField.sellingPrice:
      return t(TranslationKey.sellingPrice).singular.label;
  }
}

export function stringToOrderExportField(item: string) {
  switch (item) {
    case 'Company':
      return OrderExportField.company;
    case 'Order Id':
      return OrderExportField.orderId;
    case 'Order Created At':
      return OrderExportField.orderCreatedAt;
    case 'Order Created By':
      return OrderExportField.orderCreatedBy;
    case translate({ key: TranslationKey.destinationStockLocationName }):
      return OrderExportField.destinationStockLocationName;
    case translate({ key: TranslationKey.destinationStockLocationIdentifier }):
      return OrderExportField.destinationStockLocationIdentifier;
    case translate({ key: TranslationKey.sourceStockLocationName }):
      return OrderExportField.sourceStockLocationName;
    case translate({ key: TranslationKey.sourceStockLocationIdentifier }):
      return OrderExportField.sourceStockLocationIdentifier;
    case 'Contact Name':
      return OrderExportField.contactName;
    case 'Contact Location':
      return OrderExportField.contactLocation;
    case 'Type':
      return OrderExportField.type;
    case 'Status':
      return OrderExportField.status;
    case 'External Reference Id':
      return OrderExportField.externalReferenceId;
    case 'Estimated Time Of Arrival':
      return OrderExportField.estimatedTimeOfArrival;
    case 'Purchase Order Number':
      return OrderExportField.purchaseOrderNumber;
    case 'From Bin':
      return OrderExportField.fromBinName;
    case 'To Bin':
      return OrderExportField.toBinName;
    case 'Product Name':
      return OrderExportField.productName;
    case 'Product Number':
      return OrderExportField.productNumber;
    case 'Serial Number':
      return OrderExportField.serial;
    case 'LPN':
      return OrderExportField.lpn;
    case 'Country Of Origin':
      return OrderExportField.countryOfOrigin;
    case 'Lot Number':
      return OrderExportField.lotNumber;
    case 'Processed By':
      return OrderExportField.processedBy;
    case 'Processed At':
      return OrderExportField.processedAt;
    case 'Processed Quantity':
      return OrderExportField.processedQuantity;
    case t(TranslationKey.purchasePrice).singular.label:
      return OrderExportField.purchasePrice;
    case t(TranslationKey.sellingPrice).singular.label:
      return OrderExportField.sellingPrice;
  }
  return item;
}
